import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Layout } from '../components/Layout';
import { FadeInUp, StaggerContainer, StaggerItem } from '../components/AnimatedSection';
import { Shield, TrendingUp, MapPin, Lock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const Conclusion = () => {
  const { t } = useLanguage();

  const points = [
    { key: 'resilience', icon: Shield },
    { key: 'growth', icon: TrendingUp },
    { key: 'opportunity', icon: MapPin },
    { key: 'control', icon: Lock }
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="py-24 bg-white" data-testid="conclusion-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <span className="confidential-badge mb-6 inline-block">
              {t('conclusion.badge')}
            </span>
          </FadeInUp>
          
          <FadeInUp delay={0.1}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy mb-6" data-testid="conclusion-title">
              {t('conclusion.title')}
            </h1>
          </FadeInUp>
          
          <FadeInUp delay={0.2}>
            <p className="text-lg md:text-xl text-[#0066CC] font-medium max-w-3xl">
              {t('conclusion.subtitle')}
            </p>
          </FadeInUp>
        </div>
      </section>

      {/* Key Points */}
      <section className="py-20 bg-slate-50" data-testid="conclusion-points">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <StaggerContainer className="grid md:grid-cols-2 gap-6" staggerDelay={0.15}>
            {points.map((point) => {
              const IconComponent = point.icon;
              return (
                <StaggerItem key={point.key}>
                  <div 
                    className="bg-white border border-slate-200 p-8 hover:border-[#0066CC] transition-colors h-full"
                    data-testid={`point-${point.key}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center bg-navy">
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-navy mb-3">
                          {t(`conclusion.points.${point.key}.title`)}
                        </h3>
                        <p className="text-slate-600 leading-relaxed">
                          {t(`conclusion.points.${point.key}.text`)}
                        </p>
                      </div>
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-navy text-white" data-testid="conclusion-final">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-8">
                {t('conclusion.final.title')}
              </h2>
              <p className="text-white/80 text-lg md:text-xl leading-relaxed mb-12">
                {t('conclusion.final.text')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/sources">
                  <Button 
                    className="bg-white text-navy hover:bg-white/90 rounded-none px-8 py-6 text-sm uppercase tracking-widest font-semibold group"
                    data-testid="view-sources-btn"
                  >
                    {t('nav.sources')}
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                
                <Link to="/">
                  <Button 
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 rounded-none px-8 py-6 text-sm uppercase tracking-widest font-semibold"
                    data-testid="back-home-btn"
                  >
                    {t('nav.home')}
                  </Button>
                </Link>
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Stats Summary */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeInUp>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-6">
                <p className="text-5xl font-oswald font-bold text-navy">$175B</p>
                <p className="text-xs uppercase tracking-wider text-slate-500 mt-2">Market 2032</p>
              </div>
              <div className="text-center p-6">
                <p className="text-5xl font-oswald font-bold text-navy">20.8%</p>
                <p className="text-xs uppercase tracking-wider text-slate-500 mt-2">CAGR</p>
              </div>
              <div className="text-center p-6">
                <p className="text-5xl font-oswald font-bold text-navy">150M</p>
                <p className="text-xs uppercase tracking-wider text-slate-500 mt-2">Regional Market</p>
              </div>
              <div className="text-center p-6">
                <p className="text-5xl font-oswald font-bold text-[#0066CC]">NOW</p>
                <p className="text-xs uppercase tracking-wider text-slate-500 mt-2">Time to Act</p>
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>
    </Layout>
  );
};

export default Conclusion;
